import { OvertimeRecord } from '../types/leavePolicy';
import { isResolvedIdentity, sameIdentity } from './identity';
import { generateClient } from 'aws-amplify/api';

/**
 * Local cache of the server's overtime ledger — NOT the system of record.
 *
 * Same split as Comp-Off and HR adjustments, for the same reason: `getRecords` and
 * `getSummary` are read synchronously by the Overtime report, so localStorage caches the
 * server and `refresh()` fills it.
 *
 * This store was previously localStorage-only, which meant the Overtime report showed whatever
 * the viewer's own browser happened to hold — an employee's logged overtime never reached the
 * person who had to approve or pay it.
 */
const OVERTIME_KEY = 'creyenteone:overtime-records:v1';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const overtimeFields = `
  id employeeEmail date otType hoursWorked status approvedBy createdAt
`;

const listOvertimeQuery = /* GraphQL */ `
  query ListOvertimeRecords($limit: Int, $nextToken: String) {
    listOvertimeRecords(limit: $limit, nextToken: $nextToken) {
      items { ${overtimeFields} }
      nextToken
    }
  }
`;

const createOvertimeMutation = /* GraphQL */ `
  mutation CreateOvertimeRecord($input: CreateOvertimeInput!) {
    createOvertimeRecord(input: $input) { ${overtimeFields} }
  }
`;

type OtType = OvertimeRecord['otType'];
type OtStatus = OvertimeRecord['status'];

const OT_TYPES: OtType[] = ['WEEKDAY', 'WEEKEND', 'HOLIDAY', 'NIGHT'];
const OT_STATUSES: OtStatus[] = ['APPROVED', 'PENDING', 'REJECTED'];

const normalize = (raw: any): OvertimeRecord => {
  const otType = String(raw?.otType || '').toUpperCase() as OtType;
  const status = String(raw?.status || '').toUpperCase() as OtStatus;
  return {
    id: String(raw?.id || ''),
    employeeEmail: String(raw?.employeeEmail || ''),
    date: String(raw?.date || ''),
    otType: OT_TYPES.includes(otType) ? otType : 'WEEKDAY',
    hoursWorked: Number(raw?.hoursWorked) || 0,
    status: OT_STATUSES.includes(status) ? status : 'PENDING',
    approvedBy: raw?.approvedBy || undefined,
    createdAt: String(raw?.createdAt || ''),
  };
};

export class OvertimeService {
  /**
   * Cache read — synchronous by design, see the module note.
   *
   * No argument means the whole ledger; an employee means only theirs. An empty identity is a
   * failed lookup, not a wildcard — see the note in compOffService.getRecords for the balance
   * this reasoning was written against.
   */
  static getRecords(employeeEmail?: string): OvertimeRecord[] {
    try {
      const raw = localStorage.getItem(OVERTIME_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const records = parsed.map(normalize);
      if (employeeEmail === undefined) return records;
      if (!isResolvedIdentity(employeeEmail)) {
        console.warn(
          '[Overtime] getRecords called with an empty employee identity — returning no records.'
        );
        return [];
      }
      // Domain-aware: see the note in compOffService.getRecords.
      return records.filter(r => sameIdentity(r.employeeEmail, employeeEmail));
    } catch {
      return [];
    }
  }

  /**
   * Pull the ledger from the server into the cache.
   *
   * Returns false when the server could not be reached, so a report can say it is showing
   * cached data rather than presenting one browser's records as the company's.
   */
  static async refresh(): Promise<boolean> {
    try {
      const result = await getClient().graphql({
        query: listOvertimeQuery,
        variables: { limit: 500 },
        authMode: 'userPool' as const,
      } as any);
      const items = ('data' in result ? result.data : null)?.listOvertimeRecords?.items;
      if (!Array.isArray(items)) return false;

      // Change-detected before announcing: pages refresh on 'overtime-updated', so an
      // unconditional dispatch would have them trigger the refresh that re-fires it.
      const next = JSON.stringify(items.map(normalize));
      if (next !== localStorage.getItem(OVERTIME_KEY)) {
        localStorage.setItem(OVERTIME_KEY, next);
        window.dispatchEvent(new Event('overtime-updated'));
        window.dispatchEvent(new Event('storage'));
      }
      return true;
    } catch (error) {
      console.warn('[Overtime] refresh failed, using cached records:', error);
      return false;
    }
  }

  /**
   * Log overtime. Server first.
   *
   * `ok` reports whether it actually reached the server, so a caller can distinguish "recorded"
   * from "recorded only on this device" — the difference that previously went unnoticed because
   * there was no server to reach.
   */
  static async logOvertime(
    employeeEmail: string,
    date: string,
    otType: OtType,
    hoursWorked: number,
    status: OtStatus = 'PENDING',
    approvedBy?: string
  ): Promise<{ ok: boolean; record: OvertimeRecord }> {
    let record: OvertimeRecord | null = null;
    try {
      const result = await getClient().graphql({
        query: createOvertimeMutation,
        variables: {
          input: { employeeEmail, date, otType, hoursWorked, status, approvedBy: approvedBy || undefined },
        },
        authMode: 'userPool' as const,
      } as any);
      const raw = ('data' in result ? result.data : null)?.createOvertimeRecord;
      if (raw) record = normalize(raw);
    } catch (error) {
      console.warn('[Overtime] createOvertimeRecord failed, caching locally only:', error);
    }

    const reachedServer = record !== null;
    if (!record) {
      record = normalize({
        id: `ot-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        employeeEmail,
        date,
        otType,
        hoursWorked,
        status,
        approvedBy,
        createdAt: new Date().toISOString(),
      });
    }

    localStorage.setItem(OVERTIME_KEY, JSON.stringify([record, ...this.getRecords()]));
    window.dispatchEvent(new Event('overtime-updated'));
    window.dispatchEvent(new Event('storage'));
    return { ok: reachedServer, record };
  }

  static getSummary(employeeEmail: string): { totalHours: number; approvedHours: number; pendingHours: number } {
    const records = this.getRecords(employeeEmail);
    let total = 0;
    let approved = 0;
    let pending = 0;

    for (const r of records) {
      total += r.hoursWorked;
      if (r.status === 'APPROVED') approved += r.hoursWorked;
      if (r.status === 'PENDING') pending += r.hoursWorked;
    }

    return { totalHours: total, approvedHours: approved, pendingHours: pending };
  }
}
